import { useCallback } from 'react';
import { Transforms, Editor, Range } from 'slate';
import { ReactEditor, useSlate, useFocused } from 'slate-react';
import last from 'lodash/last';

import { isSelectionEmpty } from '@app/components/rich-text-editor/utils';
import { clientLogger } from '@flow/logging/client-logger';

interface FocusEditorOptions {
  collapse?: boolean;
}

const getEditorSelection = (editor: Editor, selection: Range, options: FocusEditorOptions = {}) => {
  const { collapse = false } = options;

  if (collapse && !Range.isCollapsed(selection)) {
    return last(Editor.edges(editor, selection));
  }

  return selection;
};

const setEditorSelectionAndFocus = (editor: Editor, selection: Range) => {
  try {
    Transforms.select(editor, selection);
    ReactEditor.focus(editor);
  } catch (error) {
    clientLogger.warn('Failed to set the editor selection', error);
  }
};

const useFocusEditor = () => {
  const editor = useSlate();
  const focused = useFocused();
  const { selection } = editor;

  const focusEditor = useCallback(
    (options: FocusEditorOptions) => {
      if (!focused && !isSelectionEmpty((selection as Range) || null)) {
        setEditorSelectionAndFocus(
          editor,
          getEditorSelection(editor, selection as Range, options) as Range,
        );
      }
    },
    [
      editor,
      focused,
      selection,
    ],
  );

  return focusEditor;
};

export default useFocusEditor;
